/**
 * Load More Block - Interactivity API View Script
 *
 * Fetches the next page from the block's own REST route and appends its rows
 * to the list that is already on the page.
 *
 * The request carries a post id, a query id and a page number — never the
 * query itself. See LoadMore.php for why.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

const { state } = store( 'brandy/load-more', {
	state: {
		get buttonLabel() {
			const ctx = getContext();
			return ctx.busy ? ctx.loadingLabel : ctx.label;
		},
	},
	actions: {
		*load() {
			const ctx = getContext();
			const { ref } = getElement();

			if ( ctx.busy || ctx.done ) {
				return;
			}

			// The list lives in the parent query, not inside this block.
			const container = ref.closest( '.wp-block-brandy-relative-posts' );
			const list = container?.querySelector( 'ul.wp-block-brandy-post-template' );

			if ( ! list ) {
				return;
			}

			ctx.busy = true;

			try {
				const url = new URL( ctx.endpoint, window.location.origin );
				url.searchParams.set( 'postId', ctx.postId );
				url.searchParams.set( 'queryId', ctx.queryId );
				url.searchParams.set( 'page', ctx.page + 1 );

				const response = yield fetch( url );

				if ( ! response.ok ) {
					throw new Error( `HTTP ${ response.status }` );
				}

				const data = yield response.json();

				// Parse into a template first: assigning to innerHTML on the
				// live list would re-parse every row already on the page and
				// throw away any state the rows are holding.
				const template = document.createElement( 'template' );
				template.innerHTML = data.items ?? '';
				const added = template.content.children.length;
				const firstNew = template.content.firstElementChild;

				list.appendChild( template.content );

				ctx.page += 1;
				ctx.done = ! data.hasMore;
				ctx.announcement = added
					? `${ added } more posts loaded.`
					: 'No more posts.';

				// Focus lands on the first new post rather than staying on a
				// button that may have just disappeared.
				if ( firstNew ) {
					firstNew.setAttribute( 'tabindex', '-1' );
					firstNew.focus( { preventScroll: true } );
				}
			} catch ( error ) {
				// A failed page should not leave a dead button: say so and let
				// the reader try again.
				ctx.announcement = 'Could not load more posts.';
			} finally {
				ctx.busy = false;
			}
		},
	},
} );
